import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import {
  phishingLanguageDisplayLabel,
  PhishingLanguagesEnum,
} from '@/api/enums/phishing-languages.enum';
import { sortAlphabetically } from '@/utils/helpers/courses.helper';
import { toBcp47 } from '@/utils/helpers/language.helper';

const DEEPL_UNSUPPORTED_LANGUAGES: LanguagesEnum[] = [LanguagesEnum.KL];

const phishingLanguageByValue = Object.values(PhishingLanguagesEnum).reduce<
  Partial<Record<string, PhishingLanguagesEnum>>
>((acc, phishingLanguage) => {
  acc[phishingLanguage] = phishingLanguage;
  return acc;
}, {});

const languageByValue = Object.values(LanguagesEnum).reduce<
  Partial<Record<string, LanguagesEnum>>
>((acc, language) => {
  acc[language] = language;
  return acc;
}, {});

export const mapLanguageToPhishingLanguage = (
  language: LanguagesEnum,
): PhishingLanguagesEnum | null => phishingLanguageByValue[language] ?? null;

export const mapPhishingLanguageToLanguage = (
  phishingLanguage: PhishingLanguagesEnum,
): LanguagesEnum | null => languageByValue[phishingLanguage] ?? null;

// Language code shown for a phishing language (DA, DE, EN, SV, NB, FI, ...),
// not the country code: da_DK -> DA.
export const getPhishingLanguageCode = (
  language: PhishingLanguagesEnum,
): string => toBcp47(language).toUpperCase();

export const getPhishingLanguageLabel = (
  language: PhishingLanguagesEnum | LanguagesEnum,
): string =>
  languageDisplayLabel[language] ??
  phishingLanguageDisplayLabel[language] ??
  language;

export const getAvailablePhishingTranslationLanguages = (
  linkedLanguages: PhishingLanguagesEnum[],
): LanguagesEnum[] =>
  Object.values(LanguagesEnum).filter((language) => {
    if (DEEPL_UNSUPPORTED_LANGUAGES.includes(language)) {
      return false;
    }

    const phishingLanguage = mapLanguageToPhishingLanguage(language);
    return !!phishingLanguage && !linkedLanguages.includes(phishingLanguage);
  });

export const getPhishingTranslationLanguageOptions = (
  linkedLanguages: PhishingLanguagesEnum[],
): { label: string; value: LanguagesEnum }[] =>
  getAvailablePhishingTranslationLanguages(linkedLanguages)
    .map((language) => ({
      label: languageDisplayLabel[language],
      value: language,
    }))
    .sort((a, b) => sortAlphabetically(a.label, b.label));
